import { css, keyframes } from '@emotion/react'
import { Theme } from '@mui/material'

const loadingWave = keyframes`
0% {
  transform: translateX(-100%);
}
100% {
  transform: translateX(100%);
}
`

export const textPlaceholderContainer = ({ theme }: { theme: Theme }) => css`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing(2)} ${theme.spacing(4)};
  padding: ${theme.spacing(2)} 0;
`

export const placeholder = ({
  width = 'auto',
  height = '1em',
  gridColumn = '1',
  theme,
}: {
  width?: string
  height?: string
  gridColumn?: string
  theme: Theme
}) => css`
  width: ${width};
  height: ${height};
  background-color: #e8e8e8; /* Grey background color */
  position: relative;
  overflow: hidden;
  border-radius: ${theme.spacing(1)};
  grid-column: span ${gridColumn};
`

export const inlinePlaceholder = ({
  width = 'auto',
  height = '1.2em',
  theme,
  brightness,
}: {
  width?: string
  height?: string
  theme: Theme
  brightness: number | undefined
}) => css`
  width: ${width};
  display: inline-block;
  height: ${height};
  background-color: #e8e8e8; /* Grey background color */
  position: relative;
  filter: brightness(${brightness ? brightness : 1});
  overflow: hidden;
  border-radius: ${theme.spacing(1)};
  top: 4px;
`

export const placeholderWave = ({ brightness }: { brightness: number | undefined }) => css`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  filter: brightness(${brightness ? brightness : 1});
  opacity: 0.5;
  background: linear-gradient(to right, #ccc0 0%, #ccc 35%, #ccc 65%, #ccc0 100%);
  animation: ${loadingWave} 3s infinite linear;
`

export const barChartPlaceholderContainer = ({ theme }: { theme: Theme }) => css`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  align-items: flex-end;
  gap: ${theme.spacing(1)};
  width: 100%;
  height: 100%;
`

export const horizontalBarChartPlaceholderContainer = ({ theme }: { theme: Theme }) => css`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${theme.spacing(2)};
  width: 100%;
  height: 100%;
  max-height: ${theme.spacing(24)};
  padding: ${theme.spacing(2)} 0;

  > div {
    flex-grow: 1;
  }
`

export const tablePlaceholderContainer = ({ theme, cols }: { theme: Theme; cols: number }) => css`
  grid-template-columns: repeat(${cols}, 1fr);
  gap: ${theme.spacing(2)};
  display: grid;
  width: 100%;
  height: 100%;
  max-height: ${theme.spacing(32)};
  padding: ${theme.spacing(2)} 0;

  > div {
    height: auto;
  }
`

export const guagePlaceholder = ({ theme }: { theme: Theme }) => css`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${theme.spacing(33)};
  height: ${theme.spacing(17)};
  background-color: #e8e8e8;
  position: relative;
  overflow: hidden;
  border-radius: ${theme.spacing(18)} ${theme.spacing(18)} ${theme.spacing(1)} ${theme.spacing(1)};
`

export const guagePlaceholderContainer = () => css`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
`

export const chartsPlaceholderContainer = ({ theme }: { theme: Theme }) => css`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing(4)} ${theme.spacing(4)};
  padding: ${theme.spacing(1)};
  height: ${theme.spacing(60)};
`

export const placeholderAxii = ({ theme }: { theme: Theme }) => css`
  position: absolute;
  bottom: ${theme.spacing(4)};
  left: ${theme.spacing(4)};
  top: ${theme.spacing(4)};
  right: ${theme.spacing(4)};
  opacity: 0;
  border-left: 2px solid #f0f0f0;
  border-bottom: 2px solid #f0f0f0;
  mix-blend-mode: multiply;
`

// keyframes
const fadeout = keyframes`
  0% {
    opacity: 1;
    transform: translateY(0);
  }
  99% {
    opacity: 0;
    transform: translateY(-10px);
  }
  100% {
    opacity: 0;
    transform: translateY(0);
    display: none;
  }
`
const fadein = keyframes`
  0% {
    opacity: 0;
    transform: translateY(0);
  }
  1% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`

export const fallBackFadeout = (wasLoading: boolean) => css`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  ${wasLoading
    ? css`
        animation: ${fadeout} 0.5s forwards;
      `
    : 'display: none;'};
`

export const childrenCss = (
  wasLoading: boolean,
  variant: string | undefined,
  width: string | undefined,
) => css`
  width: ${width ? width : '100%'};
  ${variant == 'inline' ? `display: inline-block;` : ''}
  height:  ${variant == 'inline' ? '1.2em' : '100%'};
  ${wasLoading
    ? css`
        animation: ${fadein} 0.5s forwards;
      `
    : ''};
`
